import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Reflections = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reflections`)
      .then((r) => mounted && setItems(r.data))
      .catch((e) => console.error(e))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="reflections"
      data-testid="reflections-section"
      className="relative bg-forest text-bone py-28 md:py-44 overflow-hidden"
    >
      {/* large orbital ornament, lighter on forest background */}
      <svg className="absolute top-20 -right-32 w-[560px] opacity-25 pointer-events-none" viewBox="0 0 400 400" fill="none">
        <circle cx="200" cy="200" r="190" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="2 6" />
        <ellipse cx="200" cy="200" rx="160" ry="130" stroke="#F4EFE6" strokeWidth="0.5" transform="rotate(-15 200 200)" />
        <circle cx="200" cy="200" r="100" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="1 3" />
        <ellipse cx="200" cy="200" rx="70" ry="55" stroke="#F4EFE6" strokeWidth="0.5" transform="rotate(20 200 200)" />
        <circle cx="20" cy="200" r="3" fill="#8C6A48" />
        <circle cx="380" cy="200" r="2" fill="#F4EFE6" />
        <circle cx="200" cy="20" r="2" fill="#F4EFE6" />
      </svg>
      <svg className="absolute -bottom-24 -left-20 w-[420px] opacity-20 pointer-events-none" viewBox="0 0 400 400" fill="none">
        <circle cx="200" cy="200" r="180" stroke="#F4EFE6" strokeWidth="0.5" strokeDasharray="1 5" />
        <circle cx="200" cy="200" r="100" stroke="#F4EFE6" strokeWidth="0.5" />
        <circle cx="100" cy="120" r="2" fill="#8C6A48" />
      </svg>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.12)}
          className="grid md:grid-cols-12 gap-8 items-end mb-20"
        >
          <div className="md:col-span-7">
            <motion.p variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-clay mb-4">
              ✦ &nbsp; 07 , Reflections
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="reflections-title"
              className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight"
            >
              Thinking
              <br />
              <span className="italic text-bronze">out loud.</span>
            </motion.h2>
          </div>
          <motion.p variants={fadeUp} className="md:col-span-5 text-bone/80 text-base md:text-lg font-light leading-relaxed">
            Reflections, observations, and inner world processing.{" "}
            <span className="italic font-serif text-bronze text-[1.3em]">
              Not answers
            </span>
            , simply things I&apos;ve noticed, questioned, and continue to
            live with.
          </motion.p>
        </motion.div>

        <motion.p
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="text-[10px] uppercase tracking-[0.3em] text-clay/70 mb-10 -mt-6"
        >
          ✦ &nbsp; A sample of recent reflections &nbsp; ✦
        </motion.p>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={stagger(0.15)}
          className="grid md:grid-cols-3 gap-8 md:gap-10"
        >
          {loading && (
            <div className="col-span-3 text-center py-20 text-bone/50 italic font-serif">
              Loading reflections…
            </div>
          )}
          {!loading && items.map((r, i) => (
            <motion.article
              key={r.slug}
              variants={fadeUp}
              data-testid={`reflection-card-${i}`}
              className="group relative bg-forestDeep/40 border border-bone/10 backdrop-blur-sm overflow-hidden flex flex-col h-full"
              style={{ borderTopLeftRadius: "200px", borderTopRightRadius: "200px" }}
            >
              {/* Arched cover image */}
              <Link
                to={`/reflections/${r.slug}`}
                data-testid={`reflection-link-${r.slug}`}
                className="zoom-container relative aspect-[1/1] block overflow-hidden"
                style={{ borderTopLeftRadius: "200px", borderTopRightRadius: "200px" }}
              >
                <img
                  src={r.cover_image}
                  alt={r.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-forest/30 via-transparent to-forest/80" />
                <div className="absolute top-8 left-0 right-0 text-center text-[10px] uppercase tracking-[0.3em] text-bone/90">
                  {r.reading_time}
                </div>
              </Link>

              <div className="px-7 pb-10 pt-7 flex-1 flex flex-col text-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-bronze mb-3">
                  Essay · {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="font-serif text-3xl md:text-[2rem] leading-tight">
                  <Link
                    to={`/reflections/${r.slug}`}
                    className="hover:text-clay transition-colors duration-500"
                  >
                    {r.title}
                  </Link>
                </h3>
                {r.subtitle && (
                  <p className="mt-2 italic font-serif text-bone/60 text-base">
                    {r.subtitle}
                  </p>
                )}
                <div className="my-5 w-10 h-px bg-bronze/40 mx-auto" />
                <p className="text-sm text-bone/75 leading-relaxed font-light flex-1">
                  {r.excerpt}
                </p>
                <Link
                  to={`/reflections/${r.slug}`}
                  data-testid={`reflection-read-${r.slug}`}
                  className="mt-7 text-[10px] uppercase tracking-[0.3em] text-clay link-underline self-center"
                >
                  Read the full reflection →
                </Link>
              </div>
            </motion.article>
          ))}
        </motion.div>

        {/* Substack CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          variants={fadeUp}
          className="mt-20 pt-12 border-t border-bone/15 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-clay/70 mb-4">
            ✦ &nbsp; The journal continues elsewhere &nbsp; ✦
          </p>
          <h3 className="font-serif text-3xl md:text-4xl leading-snug max-w-2xl mx-auto">
            More slow thoughts, sent gently to your inbox.
          </h3>
          <a
            href="https://substack.com/@harmoniaportal"
            target="_blank"
            rel="noreferrer"
            data-testid="reflections-substack-cta"
            className="btn-pill mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-bone/40 text-bone text-[11px] uppercase tracking-[0.22em] hover:bg-bone hover:text-forest transition-colors duration-500"
          >
            Continue reading on Substack
            <ArrowUpRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Reflections;
